import { useEditor, useValue } from "tldraw"
import { Toolbar } from "./Toolbar"
import { StickerShape } from "../../shared/StickerShape"
import { Blend, Hash, Palette, ToggleRight } from "lucide-react"
import { ColorPickerButton } from "./ColorPickerButton"
import { LDPage } from "../../shared/LDPage"
import { Color, Env, Expr } from "../../shared/Expr"
import { useCurrentPage } from "../hooks/useCurrentPage"
import { NumberPickerButton } from "./NumberPickerButton"
// import {
//   ColorPickerTriggerButton,
//   LayerStrokeWidthContainerDiv,
//   Row,
// } from "./LayerSettingsToolbar"
import { ParameterizableControl } from "./ParameterializableControl"
import Slider from "@mui/material/Slider"
import { Var } from "../icons/Var"
import PaletteOutlined from "@mui/icons-material/PaletteOutlined"
import Button from "@mui/material/Button"
import Switch from "@mui/material/Switch"
import ToggleButton from "@mui/material/ToggleButton"
import { useAppDispatch } from "../app/hooks"
import { showPageSettingsDialog } from "../features/app/appSlice"
import { LDGroupShape, LDShape } from "../../shared/LDShape"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CSSProperties,
  MenuItem,
  Select,
  styled,
  TextField,
} from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { CustomDrawShape } from "../../shared/CustomDrawShape"
import { Parameter } from "../../shared/Parameter"
import { isEqual } from "lodash"
import { SelectionListDiv, SelectionToolbar } from "./SelectionToolbar"
import { LDTextShape } from "../../shared/LDTextShape"
import { LDGeoShape } from "../../shared/LDGeoShape"
import { LDLineShape } from "../../shared/LDLineShape"
import { baseParameters } from "./LayerOverridesToolbar"
import { LDInstanceMeta } from "../../shared/LDInstance"

const Spacer = styled("div")({
  flex: 1,
})

export const Row = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 16,
})

export const ColorPickerTriggerButton = styled("button")({
  width: 30,
  height: 20,
  borderRadius: 2,
})

export const LayerStrokeWidthContainerDiv = styled("div")({
  display: "flex",
  padding: 2,
  alignItems: "center",
  marginLeft: 10,
})

const nameStyle: CSSProperties = {
  padding: "2px 4px",
  borderRadius: 3,
  margin: "0 -4px",
}

type Props = {
  stickers: (
    | CustomDrawShape
    | LDGroupShape
    | LDTextShape
    | LDGeoShape
    | LDLineShape
  )[]
}

const sliderStyles = {
  padding: "10px 0",
  width: 80,
}
const textFieldStyles = {
  width: 80,
}

export function StickerParametersToolbar({ stickers }: Props) {
  const editor = useEditor()
  const page = useCurrentPage()
  const dispatch = useAppDispatch()

  const onlySticker = stickers.length === 1 ? stickers[0] : null

  const parameters = useValue(
    "sticker parameters",
    () => {
      let result = {} as Record<
        string,
        {
          name: string
          type: "color" | "number" | "boolean" | "string"
          values: Expr<Color | number | boolean | string>[]
          min?: number
          max?: number
          step?: number
          options?: string[]
          group?: string
        }
      >

      stickers.forEach((sticker) => {
        const stickerPage =
          sticker.type === "custom-group"
            ? (editor.getPage(sticker.props.pageId) as LDPage)
            : null

        const stickerOverrides: Env = sticker.props.parameterValues

        result = {
          ...result,
          ...Object.fromEntries(
            Object.entries(
              sticker.type === "custom-group"
                ? stickerPage!.meta.parameters
                : baseParameters,
            ).map(([paramId, p]) => {
              const prevValue = result[paramId]?.values
              const newValue = stickerOverrides?.[paramId]
              if (prevValue?.find((v) => isEqual(v, newValue)))
                return [
                  paramId,
                  {
                    name: p.name,
                    type: p.type,
                    values: prevValue,
                    min: p.type === "number" ? p.min : undefined,
                    max: p.type === "number" ? p.max : undefined,
                    step: p.type === "number" ? p.step : undefined,
                    options: p.type === "string" ? p.options : undefined,
                    group: p.group,
                  },
                ] as const

              const values = prevValue
                ? newValue
                  ? [...prevValue, newValue]
                  : prevValue
                : newValue
                  ? [newValue]
                  : []

              return [
                paramId,
                {
                  name: p.name,
                  type: p.type,
                  values:
                    values.length === 0 && stickerPage
                      ? [stickerPage?.meta.parameterValues[paramId]]
                      : values,
                  min: p.type === "number" ? p.min : undefined,
                  max: p.type === "number" ? p.max : undefined,
                  step: p.type === "number" ? p.step : undefined,
                  options: p.type === "string" ? p.options : undefined,
                  group: p.group,
                  layerOverride: (
                    editor.getPage(
                      LDShape.getSourcePageId(editor, sticker),
                    ) as LDPage
                  ).meta.layers[LDShape.getLayerId(sticker)]?.layerOverrides[
                    paramId
                  ],
                },
              ] as const
            }),
          ),
        }
      })

      return result
    },
    [stickers, editor],
  )

  const instanceMeta = editor.getInstanceState().meta as LDInstanceMeta

  function handleStickerParamChange(
    paramId: string,
    value: Expr<Color> | Expr<number> | Expr<boolean>,
  ) {
    stickers.forEach((sticker) => {
      if (sticker) {
        editor.updateShape({
          ...sticker,
          props: {
            parameterValues: {
              ...sticker.props.parameterValues,
              [paramId]: value,
            },
          },
        })
        if (
          ["fillColor", "strokeColor"].includes(paramId) &&
          value.type === "lit"
        ) {
          editor.updateInstanceState({
            meta: {
              ...instanceMeta,
              [paramId]: value.value,
            },
          })
        }
      }
    })
  }

  const selectedShapeIds = useValue(
    "selected shape ids",
    () => editor.getSelectedShapeIds(),
    [editor],
  )

  if (stickers[0].parentId !== editor.getCurrentPageId()) return null

  const groupedParams: Record<string, [string, (typeof parameters)[string]][]> =
    {}
  const ungroupedParams: [string, (typeof parameters)[string]][] = []

  Object.entries(parameters).forEach(([id, p]) => {
    if (p.group) {
      if (!groupedParams[p.group]) groupedParams[p.group] = []
      groupedParams[p.group].push([id, p])
    } else {
      ungroupedParams.push([id, p])
    }
  })

  function renderParameterControl(
    id: string,
    p: (typeof parameters)[string],
    page: LDPage,
    handleStickerParamChange: (id: string, value: Expr<any>) => void,
  ) {
    const firstValue = p.values[0]
    if (!firstValue) return null
    return (
      <div key={id}>
        <div
          style={{
            fontSize: "13px",
            fontWeight: 600,
            color: "#333",
            marginBottom: 6,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          {p.type === "color" && <Palette size={14} />}
          {p.type === "number" && <Hash size={14} />}
          {p.type === "boolean" && <ToggleRight size={14} />}
          <span>{p.name}</span>
        </div>

        <Row style={{ alignItems: "center", gap: 6 }}>
          <ParameterizableControl
            type={p.type}
            title="Stroke Size"
            value={
              p.values.length === 1
                ? firstValue.type === "ref"
                  ? firstValue.id
                  : null
                : null
            }
            bound={p.values.length === 1 ? firstValue.type !== "lit" : false}
            onChange={(value) => {
              if (value === null) {
                handleStickerParamChange(
                  id,
                  Expr.lit(
                    Expr.evaluate<any>(firstValue, page.meta.parameterValues),
                  ),
                )
              } else {
                handleStickerParamChange(id, value)
              }
            }}
          >
            <LayerStrokeWidthContainerDiv
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              {p.type === "string" &&
                (() => {
                  const value = Expr.evaluate(
                    p.values[0],
                    page.meta.parameterValues,
                  )
                  const disabled = firstValue.type === "ref"

                  if (p.options) {
                    return (
                      <Select
                        size="small"
                        value={value}
                        onChange={(ev) =>
                          handleStickerParamChange(
                            id,
                            Expr.lit(ev.target.value as any),
                          )
                        }
                        variant="standard"
                        style={textFieldStyles}
                        disabled={disabled}
                      >
                        {p.options.map((opt: string) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    )
                  }

                  return (
                    <TextField
                      size="small"
                      value={value}
                      style={textFieldStyles}
                      onChange={(ev) =>
                        handleStickerParamChange(
                          id,
                          Expr.lit(ev.target.value as any),
                        )
                      }
                      variant="standard"
                      disabled={disabled}
                    />
                  )
                })()}

              {p.type === "boolean" && (
                <Switch
                  size="small"
                  checked={
                    Expr.evaluate(p.values[0], page.meta.parameterValues) ===
                    true
                  }
                  onChange={(ev) =>
                    handleStickerParamChange(id, Expr.lit(ev.target.checked))
                  }
                  disabled={firstValue.type === "ref"}
                />
              )}
              {p.type === "number" &&
                (() => {
                  const largestValue = (
                    p.values.map((potential) =>
                      Expr.evaluate(potential, page.meta.parameterValues),
                    ) as number[]
                  ).sort((a, b) => b - a)[0]

                  return (
                    <>
                      <Slider
                        size="small"
                        value={largestValue}
                        style={sliderStyles}
                        onChange={(_, value) =>
                          handleStickerParamChange(
                            id,
                            Expr.lit(Number(value) as number),
                          )
                        }
                        step={p.step ?? 0.01}
                        min={p.min ?? 1}
                        max={p.max ?? 20}
                        disabled={firstValue.type === "ref"}
                      />
                      <div
                        style={{
                          width: 15,
                          fontSize: 12,
                          fontWeight: 500,
                          textAlign: "right",
                          color: "#555",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {largestValue}
                      </div>
                    </>
                  )
                })()}

              {p.type === "color" && (
                <ColorPickerButton
                  color={p.values as Expr<Color>[]}
                  onChange={(color) => handleStickerParamChange(id, color)}
                >
                  {function (clickHandler, ref, color) {
                    const backgroundColor = Array.isArray(color)
                      ? multiColorBackground(
                          color.map((c) =>
                            Expr.evaluate(c, page.meta.parameterValues),
                          ),
                        )
                      : Expr.evaluate(color, page.meta.parameterValues)

                    return (
                      <ColorPickerTriggerButton
                        ref={ref}
                        onClick={clickHandler}
                        style={{
                          background: backgroundColor,
                          border: "1px solid #ccc",
                          borderRadius: "6px",
                          width: 34,
                          height: 20,
                          cursor: "pointer",
                          boxShadow: "inset 0 0 3px rgba(0,0,0,0.1)",
                          // display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                      >
                        {!Array.isArray(color) && color.type !== "lit" && (
                          <Var size={12} />
                        )}
                      </ColorPickerTriggerButton>
                    )
                  }}
                </ColorPickerButton>
              )}
            </LayerStrokeWidthContainerDiv>
          </ParameterizableControl>
        </Row>
      </div>
    )
  }

  return (
    <div
      style={{ display: "flex", flexDirection: "column", alignItems: "center" }}
    >
      <SelectionListDiv>{selectedShapeIds.length} selected</SelectionListDiv>
      <Toolbar direction="vertical">
        <SelectionToolbar />
        <div
          style={{
            display: "flex",
            gap: 12,
            flexDirection: "column",
            padding: "12px",
            maxHeight: "400px",
            overflow: "auto",
            //borderRadius: "12px",
            //background: "linear-gradient(145deg, #f0f0f0, #ffffff)",
          }}
        >
          <>
            {Object.entries(groupedParams).map(([group, paramEntries]) => (
              <Accordion key={group} disableGutters>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  style={{ fontWeight: 600, fontSize: 14 }}
                >
                  {group}
                </AccordionSummary>
                <AccordionDetails
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  {paramEntries.map(([id, p]) =>
                    renderParameterControl(
                      id,
                      p,
                      page,
                      handleStickerParamChange,
                    ),
                  )}
                </AccordionDetails>
              </Accordion>
            ))}

            {ungroupedParams.map(([id, p]) =>
              renderParameterControl(id, p, page, handleStickerParamChange),
            )}
          </>
          {Object.entries(parameters).length === 0 &&
            onlySticker?.type === "custom-group" && (
              <div>
                <div>No parameters</div>
                <Button
                  size="small"
                  style={{ textTransform: "none", paddingLeft: 0 }}
                  onClick={() => {
                    editor.setCurrentPage(onlySticker.props.pageId)
                    dispatch(showPageSettingsDialog())
                  }}
                >
                  Add parameter
                </Button>
              </div>
            )}
        </div>
      </Toolbar>
    </div>
  )
}

function multiColorBackground(colors: Color[]) {
  return `linear-gradient(70deg, ${colors
    .map(function (c, i) {
      const bandSize = 100 / colors.length
      return `${c} ${i * bandSize}% ${i * bandSize + bandSize}%`
    })
    .join(", ")})`
}
